// imageCompress.js
// Downscale + re-encode large images in the browser before upload.
//
// Why: product/service photos straight off a phone are often 3-8MB. The backend
// silently drops oversized files on save (the request still returns success, but
// the large images never persist), while a per-file size check let them through.
// Optimising here keeps every upload well under that limit, so images save
// reliably, and uploads are much faster.

const MAX_DIM = 1600;          // longest edge in px after resize
const QUALITY = 0.82;          // JPEG quality
const SKIP_UNDER = 900 * 1024; // leave already-small images untouched (bytes)

// iPhone camera photos. WebKit (Safari / the iOS app) decodes these in an
// <img>, so the canvas pass converts them to JPEG; other browsers can't
// decode them at all. They must ALWAYS be re-encoded — a HEIC passed through
// untouched can't be displayed on the storefront.
export const isHeicFile = (file) => {
  if (!file) return false;
  if (file.type === 'image/heic' || file.type === 'image/heif') return true;
  return !file.type && /\.hei[cf]$/i.test(file.name || '');
};

// Resize/re-encode a single image File. Returns a Promise that always resolves
// to a File (the optimised one, or the original if anything goes wrong / it's
// already small). Never rejects, so callers don't need a try/catch.
export function compressImage(file, { maxDim = MAX_DIM, quality = QUALITY } = {}) {
  return new Promise((resolve) => {
    const heic = isHeicFile(file);
    if (!file || (!heic && (!file.type || !file.type.startsWith('image/')))) return resolve(file);
    // Animated GIFs / vector SVGs must not be flattened to a single raster frame.
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') return resolve(file);

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      const scale = Math.min(1, maxDim / Math.max(width || 1, height || 1));

      // Already modest in both dimensions and file size -> keep original bytes.
      // (Never for HEIC: it must become JPEG regardless of size.)
      if (!heic && scale === 1 && file.size <= SKIP_UNDER) return resolve(file);

      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      let canvas;
      try {
        canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        // White matte so transparent PNGs don't flatten to black when re-encoded
        // as JPEG.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
      } catch (e) {
        return resolve(file);
      }

      canvas.toBlob(
        (blob) => {
          // Never upsize — except HEIC, where displayability beats size.
          if (!blob || (!heic && blob.size >= file.size)) return resolve(file);
          const base = (file.name || 'image').replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

// Compress a list of Files in parallel. Always resolves to an array of Files.
export async function compressImages(files, opts) {
  return Promise.all(Array.from(files || []).map((f) => compressImage(f, opts)));
}
